import React from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import { fade } from '@material-ui/core/styles/colorManipulator';
import { SvgIconProps } from '@material-ui/core/SvgIcon';
import classnames from 'classnames';

const styles = ({
  spacing: { unit },
  palette: { primary, secondary, text },
  custom: {
    palette: { colorSupportSteel, colorSupportSlate, colorAccentAloe, colorAccentWatermelon, colorDanger },
  },
}: Theme) =>
  createStyles({
    root: {
      padding: `${unit / 2}px ${unit}px`,
    },
    primary: {
      color: primary.main,
    },
    secondary: {
      color: secondary.main,
    },
    textPrimary: {
      color: text.primary,
    },
    supportSlate: {
      color: colorSupportSlate,
    },
    default: {
      color: colorSupportSteel,
    },
    defaultIncludeBackground: {
      background: fade(colorSupportSteel, 0.2),
    },
    // TODO unify with theme colors as mockups use colors that we don't have defined
    // https://www.figma.com/file/lWC5H3y5FUdHh3cJg3NLds/Messaging-Activity?node-id=331%3A138
    success: {
      color: '#59bf95',
    },
    successIncludeBackground: {
      background: fade('#59bf95', 0.2),
    },
    error: {
      color: '#d7797e',
    },
    errorIncludeBackground: {
      background: fade('#d7797e', 0.2),
    },
    danger: {
      color: colorDanger,
    },
    dangerIncludeBackground: {
      background: fade(colorDanger, 0.2),
    },
    info: {
      color: '#61aaff',
    },
    supportSteel: {
      color: colorSupportSteel,
    },
    infoIncludeBackground: {
      background: fade('#61aaff', 0.2),
    },
    accentAlloe: {
      color: colorAccentAloe,
    },
    accentAlloeIncludeBackground: {
      background: fade(colorAccentAloe, 0.2),
    },
    accentWatermelon: {
      color: colorAccentWatermelon,
    },
    accentWatermelonIncludeBackground: {
      background: fade(colorAccentWatermelon, 0.2),
    },
  });

export interface SvgIconWrapperProps extends Omit<SvgIconProps, 'classes' | 'color'>, WithStyles<typeof styles> {
  Icon: React.ComponentType<SvgIconProps>;
  color?:
    | 'primary'
    | 'secondary'
    | 'textPrimary'
    | 'supportSlate'
    | 'supportSteel'
    | 'accentAlloe'
    | 'accentWatermelon'
    | 'default'
    | 'success'
    | 'error'
    | 'danger'
    | 'info';
  includeBackground?: boolean;
}

const SvgIconWrapper: React.FC<SvgIconWrapperProps> = ({
  classes,
  className,
  Icon,
  color = 'default',
  includeBackground,
  ...rest
}) => {
  if (includeBackground) {
    const classNameBackground = classes[`${color}IncludeBackground` as keyof typeof classes];
    return (
      <div className={classnames(classes.root, className, classNameBackground)}>
        <Icon className={classnames(className, classes[color])} {...rest} />
      </div>
    );
  }
  return <Icon className={classnames(className, classes[color])} {...rest} />;
};

export default withStyles(styles, { name: 'LumaSvgIconWrapper' })(SvgIconWrapper);
